import { useSignin } from "api/mutations/useSignin";
import { useGetPerson } from "api/queries/useGetPerson";
import { personFieldsFragment } from "gql/graphql";
import { useCallback, useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAccountTargetUrl as buildAccountTargetUrl } from "utils/getAccountTargetUrl";
import { sanitizeReturnUrl } from "utils/sanitizeReturnUrl";

interface SigninFormValues {
	email: string;
	password: string;
	rememberMe: boolean;
}

const useController = () => {
	const { mutateAsync: signin } = useSignin();
	const { data: person } = useGetPerson();

	const [errorMessage, setErrorMessage] = useState("");

	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const sanitizedReturnUrl = sanitizeReturnUrl(searchParams.get("return_url"));
	const returnUrlParam = sanitizedReturnUrl?.url ?? null;
	const ssoLink =
		returnUrlParam ?
			`/signin/sso?return_url=${encodeURIComponent(returnUrlParam)}`
		:	"/signin/sso";

	const getAccountTargetUrl = useCallback(
		(accountIndex: number) =>
			buildAccountTargetUrl(accountIndex, returnUrlParam),
		[returnUrlParam]
	);

	const handlePerson = useCallback(
		(person: personFieldsFragment) => {
			const accountCount = person.partialAccounts.length;
			const accountZeroRequiresMFA =
				(person.partialAccounts[0]?.mfaRequired && !person.mfaAuthenticated) ??
				false;
			const accountZeroIndex = person.partialAccounts[0]?.index ?? 0;
			const targetUrl = getAccountTargetUrl(accountZeroIndex);

			if (accountCount === 0) {
				navigate(
					returnUrlParam ?
						`/choose_account?return_url=${encodeURIComponent(returnUrlParam)}`
					:	"/choose_account"
				);
			} else if (accountCount === 1 && accountZeroRequiresMFA) {
				return navigate(
					`/authenticate?return_url=${encodeURIComponent(targetUrl)}`
				);
			} else if (accountCount === 1 && !accountZeroRequiresMFA) {
				if (sanitizedReturnUrl?.isAbsolute) {
					window.location.assign(targetUrl);
					return;
				}
				return navigate(targetUrl);
			} else if (accountCount > 1) {
				// A deep link that already names an account (/u/N) makes the picker a
				// no-op, so route straight there. useRequiresAuth handles MFA downstream.
				if (returnUrlParam?.startsWith("/u/")) {
					window.location.assign(`${window.location.origin}${returnUrlParam}`);
					return;
				}
				navigate(
					returnUrlParam ?
						`/choose_account?return_url=${encodeURIComponent(returnUrlParam)}`
					:	"/choose_account"
				);
			}
		},
		[
			getAccountTargetUrl,
			navigate,
			returnUrlParam,
			sanitizedReturnUrl?.isAbsolute,
		]
	);

	/** move person forward if they are already authorized */
	useEffect(() => {
		if (person) {
			handlePerson(person);
		}
	}, [handlePerson, person]);

	const handleSignin = async (data: FieldValues) => {
		const { email, password, rememberMe } = data as SigninFormValues;

		try {
			const response = await signin({
				params: {
					email,
					password,
					remember_me: rememberMe,
				},
			});

			if (response.success && response.person) {
				setErrorMessage("");
				handlePerson(response.person);
			} else if (response.error) {
				setErrorMessage(response.error.message);
			} else {
				setErrorMessage("Error Signing In");
			}
		} catch (error) {
			setErrorMessage("Invalid email or password");
		}
	};

	return {
		errorMessage,
		handleSignin,
		ssoLink,
	};
};

export default useController;
